import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import EmailInput from './EmailInput';
import RemoveProviderModal from './RemoveProviderModal';

/**
 * @typedef {import('../types').RosterProvider} RosterProvider
 */

/**
 * Single provider row in the roster table for desktop view.
 * @param {{
 *  provider: RosterProvider,
 *  isEven: boolean,
 *  onChange: (providerCode: string, updates: Partial<RosterProvider>) => void,
 *  onRemove: (providerId: string) => void,
 *  disabled?: boolean,
 *  existingProviders?: Array<RosterProvider>
 * }} props
 */
const ProviderRosterTableRow = ({
  provider,
  isEven,
  onChange,
  onRemove,
  disabled = false,
  existingProviders = [],
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  const handleEmailInputChange = useCallback(
    /**
     * @param {string} value
     */
    (value) => {
      onChange(provider.providerCode, { email: value });
    },
    [onChange, provider.providerCode],
  );

  const handleEmailValidityChange = useCallback(
    /**
     * @param {boolean} isValid
     */
    (isValid) => {
      onChange(provider.providerCode, { validEmail: isValid });
    },
    [onChange, provider.providerCode],
  );

  const handleRemoveClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfirmRemove = useCallback(() => {
    onRemove(provider.providerCode);
  }, [onRemove, provider.providerCode]);

  // Filter out the current provider from the list to check for duplicates
  const otherProviders = useMemo(() => {
    return existingProviders.filter(
      (p) => p.providerCode !== provider.providerCode,
    );
  }, [existingProviders, provider.providerCode]);

  return (
    <>
      <tr
        className={[
          'flex items-center gap-8 self-stretch border border-gray-20 px-4 py-5',
          isEven ? 'bg-white' : 'bg-gray-10',
        ].join(' ')}
        data-testid="provider-roster-table-row"
      >
        <td className="flex items-center gap-2 w-[30%]">
          {provider.providerImageUrl?.trim() && !imageLoadError ? (
            <img
              src={provider.providerImageUrl}
              alt={provider.displayName}
              className="w-15 h-20 object-cover rounded"
              onError={() => {
                setImageLoadError(true);
              }}
            />
          ) : null}
          {/* Placeholder when no image is available or image fails to load */}
          <div
            className={`w-15 h-20 bg-gray-30 rounded flex items-center justify-center ${
              provider.providerImageUrl?.trim() && !imageLoadError
                ? 'hidden'
                : ''
            }`}
          >
            <span className="text-xs text-gray-70 font-semibold">
              {provider.displayName
                ? provider.displayName
                    .trim()
                    .split(' ')
                    .filter((n) => n.length > 0)
                    .map((n) => n[0])
                    .filter(Boolean)
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                : '?'}
            </span>
          </div>
          <div
            className="flex flex-col justify-center"
            data-testid="provider-roster-table-row-name"
          >
            <p>
              <b>{provider.displayName}</b>
            </p>
            <span className="text-sm mb-2">
              {provider.primarySpecialty?.specialtyName}
            </span>
            <span className="text-sm">NPI: {provider.npi}</span>
          </div>
        </td>
        <td className="flex flex-col w-[30%]">
          <span className="text-sm font-bold">{provider.office?.name}</span>
          <span className="text-sm w-1/2 font-thin leading-5">
            {provider.office?.addressLine}
            <br />
            {provider.office?.cityState}
          </span>
        </td>
        <td className="flex-1 flex items-start pt-1">
          <EmailInput
            value={provider.email || ''}
            onValueChange={handleEmailInputChange}
            onValidityChange={handleEmailValidityChange}
            disabled={disabled}
            otherProviders={otherProviders}
            providerEmail={provider.emailFromApi ?? ''}
          />
        </td>
        <td className="flex items-center">
          <button
            type="button"
            onClick={handleRemoveClick}
            className="w-6 h-6 cursor-pointer focus:outline-none focus:ring-2 focus:ring-coral-90 focus:ring-opacity-50 rounded"
            aria-label={`Remove ${provider.displayName} from roster`}
            data-testid="remove-provider-button"
            disabled={disabled}
          >
            <img
              src="/public/images/trash-icon.svg"
              alt=""
              className="w-6 h-6"
            />
          </button>
        </td>
      </tr>

      <RemoveProviderModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmRemove}
        providerName={provider.displayName}
      />
    </>
  );
};

ProviderRosterTableRow.propTypes = {
  provider: PropTypes.shape({
    providerCode: PropTypes.string,
    displayName: PropTypes.string,
    npi: PropTypes.string,
    providerImageUrl: PropTypes.string,
    primarySpecialty: PropTypes.shape({
      specialtyName: PropTypes.string,
      specialtyCode: PropTypes.string,
    }),
    office: PropTypes.shape({
      name: PropTypes.string,
      addressLine: PropTypes.string,
      cityState: PropTypes.string,
      phone: PropTypes.string,
      fax: PropTypes.string,
    }),
    email: PropTypes.string,
    validEmail: PropTypes.bool,
  }).isRequired,
  isEven: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  existingProviders: PropTypes.arrayOf(PropTypes.object),
};

export default ProviderRosterTableRow;
